/**
 * HMCEventsPage Component
 *
 * Houston Marketing Calendar Events Report page.
 * Features file upload, event reports viewer, report history, and modern card-based UI.
 * Phase 4 implementation for HMC Web Upload project.
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { FileUploadDropzone } from '../calendar/components/FileUploadDropzone';
import { EventReportViewer } from './components/EventReportViewer';
import { ReportHistorySidebar, type ReportStats } from './components/ReportHistorySidebar';
import { ShareLinkModal } from './components/ShareLinkModal';
import { EmailTemplateModal } from './components/EmailTemplateModal';
import { CalendarInviteModal } from './components/CalendarInviteModal';
import { RequestsTab } from './components/requests-tab';
import { useHMCEventsStore, type HMCEvent } from './hmcEventsStore';
import { GuestViewProvider, useGuestView } from './GuestViewContext';
import { getTotalTickets } from './utils/attendanceParser';
import { MobileFAB } from '@shared/components/ui/MobileFAB';
import { Upload as UploadFab } from 'lucide-react';
import { UnifiedAppShellHeader } from '@shared/components/layouts/UnifiedAppShellHeader';
import { CollapsiblePageHeader } from '@shared/components/layouts/CollapsiblePageHeader';
import { BarChart3, MailCheck } from 'lucide-react';
import { InvitationTrackingTable } from '@shared/components/InvitationTrackingTable';

// Tab type
type TabType = 'events' | 'requests' | 'invitations';

// Icons
const UploadIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const MenuIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

// Eye icon for view toggle
const EyeIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

// Eye off icon for guest view
const EyeOffIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

// Share/Link icon
const ShareIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
  </svg>
);

// Mail icon for email template
const MailIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

// Calendar icon for calendar invite
const CalendarIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

// Ticket icon for requests tab
const TicketIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
  </svg>
);

// List icon for events tab
const ListIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
  </svg>
);

// Inner component that uses guest view context
const HMCEventsPageInner: React.FC = () => {
  const { isGuestView, toggleGuestView } = useGuestView();
  const {
    loading,
    error,
    events,
    currentReport,
    uploadHistory,
    fetchUploadHistory,
    fetchReportEvents,
    uploadFile,
    uploadState,
    uploadError,
    uploadResult,
    selectedEventIds,
  } = useHMCEventsStore();

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isCalendarInviteModalOpen, setIsCalendarInviteModalOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('events');

  // Compute stats for sidebar - use text-based detection for accurate counts
  const stats: ReportStats | undefined = useMemo(() => {
    if (!events || events.length === 0) return undefined;

    // Helper to detect event type from text content
    const detectEventType = (event: HMCEvent) => {
      const eventText = `${event.opponent || ''} ${event.event_description || ''} ${event.venue || ''} ${event.special_event || ''}`.toLowerCase();
      return {
        isDynamo: event.event_type === 'dynamo' || eventText.includes('dynamo'),
        isDash: event.event_type === 'dash' || eventText.includes('dash'),
        isRockets: event.event_type === 'rockets' || eventText.includes('rockets') || eventText.includes('houston rockets'),
      };
    };

    const dynamo = events.filter((e) => detectEventType(e).isDynamo).length;
    const dash = events.filter((e) => detectEventType(e).isDash).length;
    const rockets = events.filter((e) => detectEventType(e).isRockets).length;
    const enriched = events.filter((e) => e.enrichment_status === 'enriched').length;
    const failed = events.filter((e) => e.enrichment_status === 'failed').length;
    const selected = selectedEventIds.size;
    const totalTickets = events.reduce((sum, e) => sum + getTotalTickets(e.special_event), 0);
    const eventsWithTickets = events.filter((e) => getTotalTickets(e.special_event) > 0).length;

    return { total: events.length, dynamo, dash, rockets, enriched, failed, selected, totalTickets, eventsWithTickets };
  }, [events, selectedEventIds]);

  // Fetch upload history on mount
  useEffect(() => {
    fetchUploadHistory();
  }, [fetchUploadHistory]);

  // Load latest report on mount if available
  useEffect(() => {
    if (uploadHistory.length > 0 && !currentReport) {
      fetchReportEvents(uploadHistory[0].upload_id);
    }
  }, [uploadHistory, currentReport, fetchReportEvents]);

  // Handle file upload
  const handleFileSelect = useCallback(
    async (file: File) => {
      await uploadFile(file);
      // Refresh history after upload
      fetchUploadHistory();
    },
    [uploadFile, fetchUploadHistory]
  );

  // Handle report selection
  const handleReportSelect = useCallback(
    (uploadId: string) => {
      fetchReportEvents(uploadId);
      setIsSidebarOpen(false); // Close sidebar on mobile after selection
    },
    [fetchReportEvents]
  );

  // Close upload modal and refresh on success
  const handleCloseUploadModal = useCallback(() => {
    setIsUploadModalOpen(false);
    if (uploadState === 'success' && uploadResult) {
      fetchUploadHistory();
      if (uploadResult.upload_id) {
        fetchReportEvents(uploadResult.upload_id);
      }
    }
  }, [uploadState, uploadResult, fetchUploadHistory, fetchReportEvents]);

  return (
    <div className="h-full bg-dark-bg flex flex-col overflow-hidden" data-testid="hmc-events-page">
      {/* Row 1: Unified App Shell Header */}
      {!isGuestView && <UnifiedAppShellHeader />}

      {/* Row 2: Collapsible Page Header */}
      {!isGuestView && (
        <CollapsiblePageHeader
          icon={BarChart3}
          title="HMC Events"
          description="Houston Marketing Calendar event reports and analytics"
          storageKey="hmc-events"
        />
      )}

      {/* Main layout: Sidebar + Content */}
      <div className="flex-1 flex overflow-hidden">
      {/* Sidebar - Hidden on mobile and in guest view, toggle with button */}
      {!isGuestView && (
        <aside
          className={`
            fixed inset-y-0 left-0 z-40 w-72 bg-gradient-to-b from-dark-surface/95 to-dark-bg/90 backdrop-blur-sm border-r border-dark-border/25
            transform transition-transform duration-200 ease-in-out
            lg:relative lg:translate-x-0 lg:h-screen lg:sticky lg:top-0
            flex flex-col overflow-hidden shadow-xl shadow-black/20 lg:shadow-none
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
        >
          {/* Sidebar header with close button on mobile */}
          <div className="shrink-0 flex items-center justify-between p-4 border-b border-dark-border/30 lg:hidden">
            <h2 className="text-lg font-semibold text-white">Report History</h2>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="p-2 text-dark-muted hover:text-white rounded-lg"
              aria-label="Close sidebar"
            >
              <CloseIcon />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-hidden">
            <ReportHistorySidebar
              history={uploadHistory}
              currentUploadId={currentReport?.upload_id}
              onSelect={handleReportSelect}
              loading={loading}
              stats={stats}
            />
          </div>
        </aside>
      )}

      {/* Sidebar overlay on mobile */}
      {!isGuestView && isSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* Actions toolbar */}
        <div className="flex-shrink-0 border-b border-dark-border/25 px-4 py-2">
          <div className="flex flex-wrap items-center gap-2">
              {/* Mobile sidebar toggle - hidden in guest view */}
              {!isGuestView && (
                <button
                  onClick={() => setIsSidebarOpen(true)}
                  className="p-2 text-dark-muted hover:text-white rounded-lg lg:hidden min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Open sidebar"
                >
                  <MenuIcon />
                </button>
              )}

              <div className="flex-1" />

              {/* Guest/Admin View Toggle */}
              <button
                onClick={toggleGuestView}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-150 min-h-[44px] ${
                  isGuestView
                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30'
                    : 'bg-dark-border text-dark-muted hover:text-white hover:bg-dark-border/80'
                }`}
                title={isGuestView ? 'Switch to Admin View' : 'Switch to Guest View'}
                data-testid="hmc-view-toggle"
              >
                {isGuestView ? <EyeOffIcon /> : <EyeIcon />}
                <span className="hidden sm:inline text-sm font-medium">
                  {isGuestView ? 'Guest' : 'Admin'}
                </span>
              </button>

              {/* Share button - hidden in guest view and requests tab, only shown when report selected */}
              {!isGuestView && currentReport && activeTab === 'events' && (
                <button
                  onClick={() => setIsShareModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-dark-border text-dark-text rounded-lg hover:bg-dark-border/80 hover:text-white transition-all duration-150 min-h-[44px]"
                  data-testid="hmc-share-button"
                  title="Create shareable link"
                >
                  <ShareIcon />
                  <span className="hidden sm:inline text-sm font-medium">Share</span>
                </button>
              )}

              {/* Email Template button - hidden in guest view and requests tab, only shown when report selected */}
              {!isGuestView && currentReport && events.length > 0 && activeTab === 'events' && (
                <button
                  onClick={() => setIsEmailModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 text-purple-300 border border-purple-500/30 rounded-lg hover:bg-purple-500/30 hover:text-purple-200 transition-all duration-150 min-h-[44px]"
                  data-testid="hmc-email-template-button"
                  title="Generate email template"
                >
                  <MailIcon />
                  <span className="hidden sm:inline text-sm font-medium">Email</span>
                </button>
              )}

              {/* Calendar Invite button - hidden in guest view and requests tab, only shown when report selected */}
              {!isGuestView && currentReport && events.length > 0 && activeTab === 'events' && (
                <button
                  onClick={() => setIsCalendarInviteModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-300 border border-green-500/30 rounded-lg hover:bg-green-500/30 hover:text-green-200 transition-all duration-150 min-h-[44px]"
                  data-testid="hmc-calendar-invite-button"
                  title="Generate calendar invite"
                >
                  <CalendarIcon />
                  <span className="hidden sm:inline text-sm font-medium">Invite</span>
                </button>
              )}

              {/* Tracking button - opens Requests tab, hidden in guest view */}
              {!isGuestView && activeTab === 'events' && (
                <button
                  onClick={() => setActiveTab('requests')}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-lg hover:bg-amber-500/30 hover:text-amber-200 transition-all duration-150 min-h-[44px]"
                  data-testid="hmc-tracking-button"
                  title="View ticket request tracking"
                >
                  <TicketIcon />
                  <span className="hidden sm:inline text-sm font-medium">Tracking</span>
                </button>
              )}

              {/* Upload button - hidden in guest view and requests tab */}
              {!isGuestView && activeTab === 'events' && (
                <button
                  onClick={() => setIsUploadModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-all duration-150 min-h-[44px]"
                  data-testid="hmc-upload-button"
                >
                  <UploadIcon />
                  <span className="hidden sm:inline text-sm font-medium">Upload</span>
                </button>
              )}
            </div>

            {/* Current report info - only show on events tab */}
            {currentReport && activeTab === 'events' && (
              <div className="flex flex-wrap items-center gap-3 mt-3 text-sm">
                <span className="text-dark-muted">
                  Viewing: <span className="text-white font-medium">{currentReport.filename}</span>
                </span>
                <span className="text-dark-muted/70">|</span>
                <span className="text-dark-muted">
                  {currentReport.events_created} events
                </span>
                {currentReport.enrichment_status && (
                  <>
                    <span className="text-dark-muted/70">|</span>
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        currentReport.enrichment_status === 'completed'
                          ? 'bg-green-500/20 text-green-400'
                          : currentReport.enrichment_status === 'pending'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {currentReport.enrichment_status}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

        {/* Tab navigation - hidden in guest view */}
        {!isGuestView && (
          <div className="flex-shrink-0 px-4 border-b border-dark-border/25">
            <nav className="flex gap-1" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('events')}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all duration-150 min-h-[44px] ${
                  activeTab === 'events'
                    ? 'border-purple-500 text-purple-400'
                    : 'border-transparent text-dark-muted hover:text-white hover:border-dark-border/30'
                }`}
                data-testid="hmc-tab-events"
              >
                <ListIcon />
                <span>Events</span>
              </button>
              <button
                onClick={() => setActiveTab('requests')}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all duration-150 min-h-[44px] ${
                  activeTab === 'requests'
                    ? 'border-purple-500 text-purple-400'
                    : 'border-transparent text-dark-muted hover:text-white hover:border-dark-border/30'
                }`}
                data-testid="hmc-tab-requests"
              >
                <TicketIcon />
                <span>Requests</span>
              </button>
              <button
                onClick={() => setActiveTab('invitations')}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all duration-150 min-h-[44px] ${
                  activeTab === 'invitations'
                    ? 'border-purple-500 text-purple-400'
                    : 'border-transparent text-dark-muted hover:text-white hover:border-dark-border/30'
                }`}
                data-testid="hmc-tab-invitations"
              >
                <MailCheck className="w-5 h-5" />
                <span>Invitations</span>
              </button>
            </nav>
          </div>
        )}

        {/* Content area */}
        <div className={activeTab === 'requests' || activeTab === 'invitations' ? 'flex-1 overflow-hidden' : 'p-4 md:p-6'}>
          {/* Requests Tab Content */}
          {activeTab === 'requests' && !isGuestView && (
            <RequestsTab onBackToEvents={() => setActiveTab('events')} />
          )}

          {/* Invitations Tab Content */}
          {activeTab === 'invitations' && !isGuestView && (
            <InvitationTrackingTable eventType="hmc" />
          )}

          {/* Events Tab Content */}
          {activeTab === 'events' && (
            <>
              {/* Error state */}
              {error && (
                <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-200">
                  <p className="font-medium">Error</p>
                  <p className="text-sm mt-1">{error}</p>
                </div>
              )}

              {/* Loading state */}
              {loading && !currentReport && (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500 mx-auto"></div>
                    <p className="mt-4 text-dark-muted">Loading report...</p>
                  </div>
                </div>
              )}

              {/* Empty state - no reports */}
              {!loading && uploadHistory.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-16 h-16 mb-4 bg-dark-border rounded-full flex items-center justify-center">
                    <UploadIcon />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">No Reports Yet</h3>
                  <p className="text-dark-muted mb-6 max-w-md">
                    Upload a Houston Marketing Calendar Excel file to generate your first event report.
                  </p>
                  <button
                    onClick={() => setIsUploadModalOpen(true)}
                    className="px-6 py-3 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-all duration-150 font-medium"
                  >
                    Upload Calendar File
                  </button>
                </div>
              )}

              {/* Event report viewer */}
              {currentReport && events.length > 0 && (
                <EventReportViewer
                  events={events}
                  reportInfo={currentReport}
                  loading={loading}
                />
              )}

              {/* Empty events for current report */}
              {currentReport && events.length === 0 && !loading && (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <p className="text-dark-muted">No events found in this report.</p>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      </div>      {/* FAB: Upload Report */}
      <MobileFAB icon={UploadFab} label="Upload" onPress={() => setIsUploadModalOpen(true)} />



      {/* Upload modal */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={handleCloseUploadModal}
          />

          {/* Modal content */}
          <div className="relative bg-dark-surface border border-dark-border/30 rounded-xl w-full max-w-lg p-6 shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">
                Upload Calendar File
              </h2>
              <button
                onClick={handleCloseUploadModal}
                className="p-2 text-dark-muted hover:text-white hover:bg-dark-border rounded-lg transition-all duration-150 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Close modal"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Upload dropzone or success state */}
            {uploadState === 'success' && uploadResult ? (
              <div className="text-center py-8" data-testid="hmc-upload-success">
                <div className="w-16 h-16 mx-auto mb-4 bg-green-500/20 rounded-full flex items-center justify-center text-green-400">
                  <CheckIcon />
                </div>
                <h3 className="text-lg font-medium text-white mb-2">
                  Upload Successful!
                </h3>
                <p className="text-dark-muted mb-4">
                  {uploadResult.message}
                </p>
                <div className="bg-dark-border/50 rounded-lg p-4 text-left">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-dark-muted">File:</span>
                    <span className="text-white">{uploadResult.filename}</span>
                    <span className="text-dark-muted">Events Found:</span>
                    <span className="text-white">{uploadResult.events_found}</span>
                    <span className="text-dark-muted">Events Created:</span>
                    <span className="text-white">{uploadResult.events_created}</span>
                    <span className="text-dark-muted">Status:</span>
                    <span className="text-green-400">{uploadResult.enrichment_status}</span>
                  </div>
                </div>
                <button
                  onClick={handleCloseUploadModal}
                  className="mt-6 px-6 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-all duration-150 font-medium"
                >
                  View Report
                </button>
              </div>
            ) : (
              <FileUploadDropzone
                onFileSelect={handleFileSelect}
                isUploading={uploadState === 'uploading'}
                error={uploadError}
              />
            )}

            {/* Footer info */}
            <p className="mt-4 text-xs text-dark-muted/70 text-center">
              Upload Houston Marketing Calendar Excel files (.xlsx) to generate event reports.
            </p>
          </div>
        </div>
      )}

      {/* Share link modal */}
      {currentReport && (
        <ShareLinkModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          uploadId={currentReport.upload_id}
          reportName={currentReport.filename}
          selectedEventIds={selectedEventIds}
          events={events}
          totalEventCount={events.length}
        />
      )}

      {/* Email template modal */}
      <EmailTemplateModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        events={events}
        selectedEventIds={selectedEventIds}
        uploadId={currentReport?.upload_id}
      />

      {/* Calendar invite modal */}
      <CalendarInviteModal
        isOpen={isCalendarInviteModalOpen}
        onClose={() => setIsCalendarInviteModalOpen(false)}
        events={events}
      />
    </div>
  );
};

// Main component that provides the guest view context
export const HMCEventsPage: React.FC = () => {
  return (
    <GuestViewProvider>
      <HMCEventsPageInner />
    </GuestViewProvider>
  );
};

export default HMCEventsPage;
